/**
 * 高德地图插件 - 路径规划å
 *
 * 接口：走项目统一封装 `getDriverPathPlanning`（order.driver.path.planing，eoc）。
 * 收口原则：所有路径规划解析逻辑只在本文件内处理，业务组件不应直接解析接口响应。
 *
 * 降级策略（以下情况均返回 source='placeholder' 的直连路线）：
 *   - 请求失败 / 超时（默认 8s，与 umi-request timeout 一致）
 *   - ret !== 0 或 Promise reject
 *   - 响应体无法解析出有效折线点
 *
 * 请求：由 points 映射为 lat_lon_start、lat_lon_end、waypoints（lng,lat;…）
 * 响应：data.paths[].steps[].polyline 为「lng,lat;lng,lat;…」；失败时仍兼容旧结构 parseRouteResponse
 * 坐标系：GCJ02
 *
 * 说明：
 * - 本文件只关心“路线数据抽取”，不关心地图渲染细节
 * - 业务组件只消费统一结构 RoutePathPlaceholder，避免各页面重复解析接口
 *
 * 请求映射示例：
 * points = [A, B, C, D] =>
 * {
 *   lat_lon_start: A,
 *   lat_lon_end: D,
 *   waypoints: "B.lon,B.lat;C.lon,C.lat"
 * }
 */
import { getDriverPathPlanning } from '@/apis/apis'
import type { RoutePathPlaceholder, RoutePlanInput, AmapLngLatPoint } from './types'

const EARTH_RADIUS = 6378137 // 地球半径
/** 默认行驶速度（m/s），约 35km/h，用于占位 duration 计算 */
const DEFAULT_SPEED_MPS = 9.72

const toRadians = (value: number) => {
  return (value * Math.PI) / 180
}

/** 使用 Haversine 公式计算两点球面距离（单位：米） */
const calcDistanceBetweenPoints = (start: AmapLngLatPoint, end: AmapLngLatPoint) => {
  const latDiff = toRadians(end.lat - start.lat)
  const lonDiff = toRadians(end.lon - start.lon)
  const startLatRad = toRadians(start.lat)
  const endLatRad = toRadians(end.lat)

  const haversine =
    Math.sin(latDiff / 2) * Math.sin(latDiff / 2) +
    Math.cos(startLatRad) *
      Math.cos(endLatRad) *
      Math.sin(lonDiff / 2) *
      Math.sin(lonDiff / 2)

  return 2 * EARTH_RADIUS * Math.asin(Math.sqrt(haversine))
}

const calcPathDistance = (path: AmapLngLatPoint[]) => {
  if (path.length < 2) {
    return 0
  }

  return path.reduce((accumulator, point, index) => {
    if (index === 0) {
      return accumulator
    }
    return accumulator + calcDistanceBetweenPoints(path[index - 1], point)
  }, 0)
}

/** 接口未配置或请求失败时的降级路线（直连输入坐标） */
const createPlaceholderRoute = (points: AmapLngLatPoint[]): RoutePathPlaceholder => {
  const distance = calcPathDistance(points)
  const duration = distance > 0 ? Math.ceil(distance / DEFAULT_SPEED_MPS) : 0
  return {
    path: points,
    distance,
    duration,
    source: 'placeholder',
  }
}

/** 将 points 转为 order.driver.path.planing 入参 */
const buildDriverPathPlanningBody = (points: AmapLngLatPoint[]) => {
  const formatCoordinate = (value: number) => {
    // 接口约定小数点后 14 位：这里使用截断而非四舍五入，避免 toFixed 造成拐点偏移
    const numeric = String(Number(value))
    const [integerPart, decimalPart = ''] = numeric.split('.')
    if (!decimalPart) {
      return integerPart
    }
    return `${integerPart}.${decimalPart.slice(0, 14)}`
  }
  const head = points[0]
  const tail = points[points.length - 1]
  const body: {
    lat_lon_start: { lat: number; lon: number }
    lat_lon_end: { lat: number; lon: number }
    waypoints?: string
  } = {
    lat_lon_start: {
      lat: Number(formatCoordinate(head.lat)),
      lon: Number(formatCoordinate(head.lon)),
    },
    lat_lon_end: {
      lat: Number(formatCoordinate(tail.lat)),
      lon: Number(formatCoordinate(tail.lon)),
    },
  }
  if (points.length > 2) {
    // 途经点格式：lng,lat;lng,lat（接口文档约定）
    body.waypoints = points
      .slice(1, -1)
      .map((p) => `${formatCoordinate(p.lon)},${formatCoordinate(p.lat)}`)
      .join(';')
  }
  return body
}

/** 解析单段「lng,lat」字符串为坐标对象 */
const parsePolylineLngLatPair = (segment: string): AmapLngLatPoint | null => {
  const trimmed = segment.trim()
  if (!trimmed) {
    return null
  }
  const [lngStr, latStr] = trimmed.split(',')
  const lon = Number(lngStr)
  const lat = Number(latStr)
  if (Number.isNaN(lon) || Number.isNaN(lat)) {
    return null
  }
  return { lon, lat }
}

/**
 * 解析 order.driver.path.planing 出参（主流程）：
 * - 优先解析 steps[].polyline（真实规划路线）
 * - 输出去重后的 path，避免 step 连接点重复
 */
const parseDriverPathPlanningResponse = (
  response: unknown
): RoutePathPlaceholder | null => {
  if (!response || typeof response !== 'object') {
    return null
  }
  const root = response as Record<string, unknown>
  const data = root.data as unknown
  let firstPath: Record<string, unknown> | null = null

  // 兼容两种返回：
  // 1) { data: { paths: [...] } }（高德标准路径）
  // 2) { data: [ { steps: [...] } ] }（项目网关透传后的简化结构）
  if (Array.isArray(data) && data.length > 0) {
    firstPath = data[0] as Record<string, unknown>
  } else if (data && typeof data === 'object') {
    const paths = (data as Record<string, unknown>).paths as unknown[] | undefined
    if (Array.isArray(paths) && paths.length > 0) {
      firstPath = paths[0] as Record<string, unknown>
    }
  }
  if (!firstPath) {
    return null
  }

  const steps = firstPath?.steps as unknown[] | undefined
  if (!Array.isArray(steps) || steps.length === 0) {
    return null
  }

  const pathPoints: AmapLngLatPoint[] = []
  for (const step of steps) {
    const poly = (step as Record<string, unknown>)?.polyline
    if (typeof poly === 'string' && poly) {
      for (const part of poly.split(';')) {
        const nextPoint = parsePolylineLngLatPair(part)
        if (nextPoint) {
          const prev = pathPoints[pathPoints.length - 1]
          const isDup =
            prev && prev.lon === nextPoint.lon && prev.lat === nextPoint.lat
          if (!isDup) {
            // step 之间的衔接点可能重复，去重后再喂给 polyline
            pathPoints.push(nextPoint)
          }
        }
      }
    }
  }

  if (pathPoints.length < 2) {
    return null
  }

  const distance =
    Number(firstPath.distance) || calcPathDistance(pathPoints)
  const duration = Number(firstPath.duration) || 0

  return {
    path: pathPoints,
    distance,
    duration,
    source: 'remote',
    raw: root,
  }
}

/** 兼容旧返回结构：path / polyline / points */
const parseRouteResponse = (response: unknown): RoutePathPlaceholder | null => {
  if (!response || typeof response !== 'object') {
    return null
  }

  const payload = response as any
  const route = payload?.data || payload
  // 兼容 path / polyline / points 等不同字段名，接口确认后统一
  const pathList = route?.path || route?.polyline || route?.points
  if (!Array.isArray(pathList) || !pathList.length) {
    return null
  }

  const path = pathList
    .map((item: any) => {
      // 兼容 {lon,lat} / {lng,lat} / {x,y} / [lng,lat] 四种格式
      const lon = Number(item?.lon ?? item?.lng ?? item?.x ?? item?.[0])
      const lat = Number(item?.lat ?? item?.y ?? item?.[1])
      if (Number.isNaN(lon) || Number.isNaN(lat)) {
        return null
      }
      return { lon, lat }
    })
    .filter(Boolean) as AmapLngLatPoint[]

  if (!path.length) {
    return null
  }

  const distance = Number(route?.distance) || calcPathDistance(path)
  const duration = Number(route?.duration) || 0

  return {
    path,
    distance,
    duration,
    source: 'remote',
    raw: payload,
  }
}

/**
 * 请求路径规划（项目接口 order.driver.path.planing），失败时自动降级
 * @param options.points 路线关键点（首点=起点，末点=终点，中间点=途经点）
 * @param options.timeoutMs 请求超时，默认 8000ms
 * @returns remote: 接口规划路线；placeholder: 失败时直连路线
 */
export const fetchRoutePlan = async (
  options: RoutePlanInput
): Promise<RoutePathPlaceholder> => {
  const { points, timeoutMs = 8000 } = options
  // 点位不足（<2）无法规划路线，直接降级为占位结果
  if (points.length < 2) {
    return createPlaceholderRoute(points)
  }

  try {
    // 接口失败时不弹全局错误，由地图端统一降级为 placeholder 路线
    const response = await getDriverPathPlanning(
      buildDriverPathPlanningBody(points),
      { showError: false, timeout: timeoutMs }
    )
    const parsedRoute =
      parseDriverPathPlanningResponse(response) ?? parseRouteResponse(response)
    // 接口结构变更/异常时解析失败，回退占位路线保证地图可用
    if (!parsedRoute) {
      return createPlaceholderRoute(points)
    }
    return parsedRoute
  } catch {
    // 网络错误、超时、ret!=0 等场景统一兜底
    return createPlaceholderRoute(points)
  }
}

/** 对外暴露两点距离计算，供 useAmapRoutePolling 做位移阈值判断 */
export const calcPointDistance = calcDistanceBetweenPoints
